/* eslint-disable no-unused-vars */
import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { Button, Dropdown, Tag, Tooltip } from 'antd';
import {
  MoreOutlined,
  UserOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SwapOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Droppable } from '@hello-pangea/dnd';
import { getListStyle, stringToColorHex } from '../../utils/common';
import DriverListSteps from './Steps';
import { ACTION_ROUTE, ACTION_ROUTE_DISPLAY, COLORS } from '../../constants';
import IconHide from './IconHide';
import { ReactSVG } from 'react-svg';
import Icons from '../../icons';
import { Link, useNavigate } from 'react-router-dom';
import PartnerLogo from '../../components/PartnerLogo';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import DriverAvatar from '../../components/DriverAvatar';

const DriverList = ({
  hours,
  oneHourPx,
  isDragging,
  idsHide = [],
  routePlan,
  minutes,
  startTime,
  solutions,
  routes,
  stepCompleted,
  stepsHovered,
  onHoverStep,
  handleCloseStepPopup,
  onClickStep,
  setStepsHovered,
  onClickRoute,
  setShowMoreRoute,
  onClickHide,
  onClickLock,
  idsLock,
  onClickSwap,
  selectedMultipleSteps,
  multipleStepState,
  selectedStep,
  revertDelivery,
  onSoloDriver,
}) => {
  const { t } = useTranslation('routes');
  const navigate = useNavigate();
  const onClickDriver = (routeId) => {
    navigate(`/admin/routes?q=${routeId}`);
  };
  // How many drivers are currently shown (used to label the solo toggle).
  const visibleDriverCount = (routes || []).filter((r) => !idsHide?.includes(r.driver?.id)).length;
  return (
    <>
      {routes
        .sort((a, b) => a.driver.name.localeCompare(b.driver.name))
        .map((route, index) => {
          const solution = isEmpty(solutions) ? {} : solutions[route.driver.id];
          const steps = solution || [];
          // OVO day-plan planned/actual split: a started route (driver has
          // begun — START/PAUSE/RESUME/COMPLETED) gets a second, clean "planned"
          // reference row stacked above the "actual" (log-derived) one. Routes
          // not yet started show only the single row (their actual === planned).
          const plannedSteps = isEmpty(solutions) ? [] : solutions[`planned_${route.driver.id}`] || [];
          const hasActualTimeline = [
            ACTION_ROUTE.START,
            ACTION_ROUTE.PAUSE,
            ACTION_ROUTE.RESUME,
            ACTION_ROUTE.COMPLETED,
          ].includes(route.actionStatus);

          const workingPercent = route.workingPercent || 0;
          const percentLevel = 'high';
          const color = COLORS[route.driver.id % COLORS.length];
          const isLock = idsLock?.includes(route.id);
          const isHide = idsHide?.includes(route.driver.id);
          // A route is "terminated" once the driver has explicitly ended
          // it (actionStatus = COMPLETED). Same signal the rest of this
          // view already uses (see Steps.jsx for the actual-vs-planned
          // shift swap). We grey the whole row out + block drops in
          // onDragEnd so the dispatcher can't accidentally add work to
          // a route that's already in the books.
          const isRouteCompleted = route.actionStatus === ACTION_ROUTE.COMPLETED;

          // Partner badges — every partner with at least one order on the
          // route, same look as the Routes page partner tags. partnerRef
          // (incl. logo) comes on the day-plan payload since OVO-149.
          const routePartners = Object.values(
            (route.orders || []).reduce((acc, o) => {
              if (o?.partnerRef) acc[o.partnerRef.id] = o.partnerRef;
              return acc;
            }, {}),
          ).sort((a, b) => String(a.name).localeCompare(String(b.name)));

          return (
            <div
              key={index}
              className={`tr${isRouteCompleted ? ' is-completed' : ''}${
                hasActualTimeline && !isHide ? ' has-dual-timeline' : ''
              }`}
              style={{
                minWidth: (hours.length + 1) * oneHourPx,
                width: '100%',
              }}
            >
              <div className={`td ${isDragging && 'isDragging'}`}>
                <div className="driver-name">
                  <IconHide isHide={isHide} onClickHide={() => onClickHide(route.driver.id)} />
                  <div className="driver-info">
                    <Tooltip
                      title={
                        <>
                          {route.driver.name}
                          {route.dispatchedAt ? ` (${t('dispatched')})` : ''}
                          {route.skills?.length > 0 && (
                            <div style={{ marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 4 }}>
                              {t('infoWindow.skills')}: {route.skills.join(', ')}
                            </div>
                          )}
                        </>
                      }
                      overlayInnerStyle={{ width: 200 }}
                      placement="top"
                    >
                      <span
                        className={`name ${route.actionStatus?.toLowerCase()}`}
                        onClick={() => onClickDriver(route.id)}
                      >
                        {route?.driver.name || ''}
                        {route.dispatchedAt ? ` (${t('dispatched')})` : ''}
                      </span>
                    </Tooltip>
                    {route.id && (
                      <Link
                        to={`/admin/routes/${route.id}`}
                        className="route-id-label"
                        // Own line, left-aligned — inline after the name it was
                        // pushed to the cell's right edge and clipped by the
                        // fixed-width .driver-name overflow:hidden (last digit cut).
                        style={{
                          display: 'block',
                          width: 'fit-content',
                          fontSize: 12,
                          color: '#555',
                          fontWeight: 600,
                          lineHeight: '15px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('routeLabel', { id: route.id })}
                      </Link>
                    )}
                    <div className="capacity">
                      <div className="text">
                        {route.actionStatus === ACTION_ROUTE.COMPLETED
                          ? `${moment(route.actualShiftStart).format('HH:mm')} - ${moment(route.actualShiftEnd).format('HH:mm')}`
                          : `${route.shiftStart || 'N/A'} - ${route.shiftEnd || 'N/A'}`}
                      </div>
                      {isRouteCompleted && (
                        <Tag color="success" className="completed-badge">
                          {t('completedBadge')}
                        </Tag>
                      )}
                    </div>
                    {routePartners.length > 0 && (
                      <div className="route-partners">
                        {routePartners.map((p) => (
                          // Partner CODE (full name on hover) — names are too
                          // noisy in the narrow driver cell.
                          <Tooltip key={p.id} title={p.name}>
                            <Tag
                              color="blue"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 11,
                                lineHeight: '16px',
                                padding: '0 4px',
                                marginInlineEnd: 0,
                              }}
                            >
                              <PartnerLogo name={p.name} logoKey={p.logoFile?.key} size={14} />
                              {p.code || p.name}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Driver avatar — to the RIGHT of the name/route/shift
                      block (was a small 20px chip squeezed on the left). */}
                  <span style={{ display: 'inline-flex', marginLeft: 'auto', marginRight: 6 }}>
                    <DriverAvatar name={route.driver.name} photoKey={route.driver.photoFile?.key} size={36} />
                  </span>

                  {/* Unified driver actions (replaces the avatar-click solo +
                      loose Restore icon): solo / hide / reverse / details. */}
                  <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    menu={{
                      items: [
                        {
                          key: 'solo',
                          icon: <UserOutlined />,
                          label: !isHide && visibleDriverCount === 1 ? t('showAllDrivers') : t('soloDriver'),
                          onClick: () => onSoloDriver && onSoloDriver(route.driver.id),
                        },
                        {
                          key: 'hide',
                          icon: isHide ? <EyeOutlined /> : <EyeInvisibleOutlined />,
                          label: isHide ? t('showDriver') : t('hideDriver'),
                          onClick: () => onClickHide(route.driver.id),
                        },
                        {
                          key: 'reverse',
                          icon: <SwapOutlined />,
                          label: t('reverseDeliveryOrder'),
                          onClick: () => revertDelivery(route.driver.id),
                        },
                        ...(route.id
                          ? [
                              {
                                key: 'details',
                                icon: <InfoCircleOutlined />,
                                label: t('routeDetails'),
                                onClick: () => navigate(`/admin/routes/${route.id}`),
                              },
                            ]
                          : []),
                      ],
                    }}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreOutlined />}
                      title={t('driverActions')}
                      // Snug against the column divider; the avatar before it
                      // carries the marginLeft:auto that pushes both right.
                      style={{ marginRight: -8 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>
              </div>
              <div className={`td td-timelines${hasActualTimeline && !isHide ? ' dual' : ''}`}>
                {/* Hidden driver: keep the name + eye row (so it can be toggled
                    back on) but render NO timeline content — its shift line +
                    badges would otherwise extend the scroll width and defeat
                    the single-driver axis fit. */}
                {!isHide && (
                  <>
                    {hasActualTimeline && (
                      <div className="planned-timeline">
                        <span className="timeline-label planned-label">{t('plannedTimeline')}</span>
                        <DriverListSteps
                          route={route}
                          steps={plannedSteps}
                          minutes={minutes}
                          startTime={startTime}
                          color={color}
                          solutions={solutions}
                          stepCompleted={stepCompleted}
                          oneHourPx={oneHourPx}
                          stepsHovered={stepsHovered}
                          onHoverStep={onHoverStep}
                          handleCloseStepPopup={handleCloseStepPopup}
                          onClickStep={onClickStep}
                          setStepsHovered={setStepsHovered}
                          onClickRoute={onClickRoute}
                          setShowMoreRoute={setShowMoreRoute}
                          routePlan={routePlan}
                          selectedMultipleSteps={selectedMultipleSteps}
                          multipleStepState={multipleStepState}
                          driverId={route.driver.id}
                          selectedStep={selectedStep}
                          timeMode="planned"
                          readOnly
                          solutionKey={`planned_${route.driver.id}`}
                        />
                      </div>
                    )}
                    <Droppable
                      key={`${route.driver.id}`}
                      droppableId={`${route.driver.id}`}
                      isDropDisabled={idsHide.includes(route.driver.id)}
                      direction="horizontal"
                    >
                      {(provided, snapshot) => (
                        <div
                          className={`timeline-drop ${isDragging && 'isDragging'}`}
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={{
                            ...getListStyle(isDragging, snapshot.isDraggingOver),
                          }}
                        >
                          {hasActualTimeline && (
                            <span className="timeline-label actual-label">{t('actualTimeline')}</span>
                          )}
                          <DriverListSteps
                            route={route}
                            steps={steps}
                            minutes={minutes}
                            startTime={startTime}
                            color={color}
                            solutions={solutions}
                            stepCompleted={stepCompleted}
                            oneHourPx={oneHourPx}
                            stepsHovered={stepsHovered}
                            onHoverStep={onHoverStep}
                            handleCloseStepPopup={handleCloseStepPopup}
                            onClickStep={onClickStep}
                            setStepsHovered={setStepsHovered}
                            onClickRoute={onClickRoute}
                            setShowMoreRoute={setShowMoreRoute}
                            routePlan={routePlan}
                            selectedMultipleSteps={selectedMultipleSteps}
                            multipleStepState={multipleStepState}
                            driverId={route.driver.id}
                            selectedStep={selectedStep}
                            timeMode="actual"
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </>
                )}
              </div>
            </div>
          );
        })}
    </>
  );
};

export default DriverList;
